import type { WalletPortfolio, WireHolding } from '../api/portfolio'
import { canonicalTonAddress } from './tonAddress'

function addNum(left?: string, right?: string): string {
  const a = Number(left ?? 0)
  const b = Number(right ?? 0)
  const sum = (Number.isFinite(a) ? a : 0) + (Number.isFinite(b) ? b : 0)
  return String(sum)
}

function addRaw(left?: string, right?: string): string {
  try {
    return (BigInt(left ?? '0') + BigInt(right ?? '0')).toString()
  } catch {
    return addNum(left, right)
  }
}

/** A balance one wallet does not know (sent empty: its decimals not known,
 *  a read that failed — positions_read.go) leaves the sum not known, as its
 *  value does: never the other wallets' part said as the whole, and never a
 *  "0" that would read as a closed position (r9x-5 review F2). */
function addBalance(left?: string, right?: string): string {
  const known = (v?: string) => v != null && v.trim() !== '' && Number.isFinite(Number(v))
  return known(left) && known(right) ? addRaw(left, right) : ''
}

/** Optional GRAM figures cannot become a known subtotal when one is absent. */
function addKnown(left?: string, right?: string): string | undefined {
  if (left == null || right == null || left.trim() === '' || right.trim() === '') return undefined
  const a = Number(left)
  const b = Number(right)
  return Number.isFinite(a) && Number.isFinite(b) ? String(a + b) : undefined
}

/** A required figure (the wire's string) that one wallet does not know —
 *  sent empty (positions L5: a basis not known) — stays empty, never a 0
 *  that reads as data. */
function addKnownText(left?: string, right?: string): string {
  return addKnown(left, right) ?? ''
}

/** A required USD total over wallets: "" when any wallet sent it as "". */
function sumStated(values: (string | undefined)[]): string {
  if (values.some((v) => v != null && v.trim() === '')) return ''
  return values.reduce<string>((sum, value) => addNum(sum, value), '0')
}

function sumKnown(values: (string | undefined)[]): string | undefined {
  return values.reduce<string | undefined>((sum, value) => addKnown(sum, value), '0')
}

function addQuantity(
  a: WireHolding,
  b: WireHolding,
  key: 'bought_token_raw' | 'sold_token_raw'
): string | undefined {
  const left = a[key]
  const right = b[key]
  if (
    a.token_decimals == null ||
    a.token_decimals !== b.token_decimals ||
    left == null ||
    right == null ||
    !/^\d+$/.test(left) ||
    !/^\d+$/.test(right)
  )
    return undefined
  return (BigInt(left) + BigInt(right)).toString()
}

/** backend fillTotalPnLPct: total GRAM PnL / (bought GRAM + fees GRAM). */
function pooledReturn(h: WireHolding): string | undefined {
  const raw = [h.total_pnl_ton, h.bought_ton, h.fees_ton]
  if (raw.some((v) => v == null || v.trim() === '' || !Number.isFinite(Number(v)))) return undefined
  const [pnl, bought, fees] = raw.map(Number) as [number, number, number]
  return bought >= 0 && fees >= 0 && bought + fees > 0
    ? String((pnl / (bought + fees)) * 100)
    : undefined
}

// basis_status from best to worst; a status not listed counts as worst.
const STATUS_ORDER = ['complete', 'partial', 'pending', 'no_trades', 'unknown', 'unavailable']

function worseStatus(a?: string, b?: string): string | undefined {
  if (a === undefined || b === undefined) return a ?? b
  const rank = (s: string) =>
    STATUS_ORDER.includes(s) ? STATUS_ORDER.indexOf(s) : STATUS_ORDER.length
  return rank(b) > rank(a) ? b : a
}

/** The positions-reader fields of a merged row: the units unrealized is
    valued on (valued_known_raw) add up only when both wallets state them —
    else none, and the live reprice leaves the PnL as sent; the worse
    basis_status; the union of pnl_reasons. */
function mergePositionsFields(
  into: WireHolding,
  extra: WireHolding
): Pick<WireHolding, 'basis_status' | 'pnl_reasons' | 'valued_known_raw'> {
  const out: Pick<WireHolding, 'basis_status' | 'pnl_reasons' | 'valued_known_raw'> = {}
  const status = worseStatus(into.basis_status, extra.basis_status)
  if (status !== undefined) out.basis_status = status
  if (into.pnl_reasons !== undefined || extra.pnl_reasons !== undefined) {
    out.pnl_reasons = [
      ...new Set([...(into.pnl_reasons ?? []), ...(extra.pnl_reasons ?? [])]),
    ].sort()
  }
  out.valued_known_raw =
    into.valued_known_raw !== undefined && extra.valued_known_raw !== undefined
      ? addRaw(into.valued_known_raw, extra.valued_known_raw)
      : undefined
  return out
}

function mergeHolding(into: WireHolding, extra: WireHolding): WireHolding {
  const balance = addBalance(into.balance, extra.balance)
  const balanceKnown = balance !== ''
  const merged = {
    ...into,
    ...mergePositionsFields(into, extra),
    // a wallet whose figures for it are withheld leaves the sum unknown: the
    // row is withheld whole ("Position unavailable"), never one wallet's
    // figures shown as the whole (r9x-3 review F1)
    position_unavailable: into.position_unavailable || extra.position_unavailable || undefined,
    unavailable_reason: into.unavailable_reason || extra.unavailable_reason,
    // Aggregate age covers every contributing wallet; missing coverage stays unknown.
    first_acquired_at: mergeAgeStamp(into.first_acquired_at, extra.first_acquired_at, 'first'),
    last_activity_at: mergeAgeStamp(into.last_activity_at, extra.last_activity_at, 'last'),
    hold_time_seconds: mergeHoldSeconds(into.hold_time_seconds, extra.hold_time_seconds),
    acquisition_basis_usd: addKnown(into.acquisition_basis_usd, extra.acquisition_basis_usd),
    bought_token_raw: addQuantity(into, extra, 'bought_token_raw'),
    sold_token_raw: addQuantity(into, extra, 'sold_token_raw'),
    bought_ton: addKnown(into.bought_ton, extra.bought_ton),
    sold_ton: addKnown(into.sold_ton, extra.sold_ton),
    fees_ton: addKnown(into.fees_ton, extra.fees_ton),
    remaining_cost_basis_ton: addKnown(
      into.remaining_cost_basis_ton,
      extra.remaining_cost_basis_ton
    ),
    realized_pnl_ton: addKnown(into.realized_pnl_ton, extra.realized_pnl_ton),
    acquisition_basis_ton: addKnown(into.acquisition_basis_ton, extra.acquisition_basis_ton),
    unrealized_pnl_ton: addKnown(into.unrealized_pnl_ton, extra.unrealized_pnl_ton),
    total_pnl_ton: addKnown(into.total_pnl_ton, extra.total_pnl_ton),
    balance,
    // positions L5 rows with no position send bought / sold "" (history
    // before the tape unknown): one unknown makes the sum unknown, never "0"
    bought_usd: addKnownText(into.bought_usd, extra.bought_usd),
    sold_usd: addKnownText(into.sold_usd, extra.sold_usd),
    // fees, the basis and PnL a wallet may not know (positions L5): one
    // unknown makes the sum unknown — shown "—", never $0
    fees_usd: addKnown(into.fees_usd, extra.fees_usd),
    remaining_cost_basis_usd: addKnownText(
      into.remaining_cost_basis_usd,
      extra.remaining_cost_basis_usd
    ),
    realized_pnl_usd: addKnownText(into.realized_pnl_usd, extra.realized_pnl_usd),
    // a value one wallet does not know (its balance not known, or withheld)
    // leaves the sum's value not known too (review F2)
    current_value_usd: balanceKnown
      ? addNum(into.current_value_usd, extra.current_value_usd)
      : addKnownText(into.current_value_usd, extra.current_value_usd),
    unrealized_pnl_usd: addKnownText(into.unrealized_pnl_usd, extra.unrealized_pnl_usd),
    total_pnl_usd: addKnownText(into.total_pnl_usd, extra.total_pnl_usd),
    current_value_ton: balanceKnown
      ? addNum(into.current_value_ton, extra.current_value_ton)
      : addKnownText(into.current_value_ton, extra.current_value_ton),
    price_available: into.price_available && extra.price_available,
    last_applied_wallet_event_id: Math.max(
      into.last_applied_wallet_event_id,
      extra.last_applied_wallet_event_id
    ),
  }
  merged.total_pnl_pct = pooledReturn(merged)
  return merged
}

function mergeAgeStamp(
  a: string | undefined,
  b: string | undefined,
  edge: 'first' | 'last'
): string | undefined {
  if (!a || !b || !Number.isFinite(Date.parse(a)) || !Number.isFinite(Date.parse(b)))
    return undefined
  return (edge === 'first' ? Date.parse(a) <= Date.parse(b) : Date.parse(a) >= Date.parse(b))
    ? a
    : b
}

function mergeHoldSeconds(a: number | undefined, b: number | undefined): number | undefined {
  if (a == null || b == null || !Number.isFinite(a) || !Number.isFinite(b) || a < 0 || b < 0)
    return undefined
  return Math.max(a, b)
}

/** Whether a wallet may still hold the token: a balance above zero, or one
 *  not known — an unknown never counts as sold (it would let a Sell treat a
 *  row as another wallet's alone). */
function mayHold(h: WireHolding): boolean {
  if (h.position_unavailable) return true
  const raw = h.balance?.trim() ?? ''
  if (!/^\d+$/.test(raw)) return true
  return BigInt(raw) > 0n
}

/** `viewed`: how many wallets the view sums. Fewer parts than that (a
 *  ticked wallet not read yet, or not answering): what the missing ones hold
 *  is not known, so no row says who may still hold it (client_held_in) —
 *  a Sell then never reads a row as the primary's alone. */
export function mergeWalletPortfolios(
  parts: WalletPortfolio[],
  viewed = parts.length
): WalletPortfolio | null {
  if (parts.length === 0) return null
  const first = parts[0]
  if (parts.length === 1) return first ?? null
  if (!first) return null
  const byMaster = new Map<string, WireHolding>()
  // which wallets each row stands for: a view over several wallets tells them
  // apart (the rows' wallet marks; a Sell only from a row the primary alone
  // may still hold)
  const walletsOf = new Map<string, { all: string[]; held: string[] }>()
  for (const part of parts) {
    for (const holding of part.holdings) {
      const key = canonicalTonAddress(holding.token_master) ?? holding.token_master
      const current = byMaster.get(key)
      byMaster.set(key, current ? mergeHolding(current, holding) : holding)
      const tags = walletsOf.get(key) ?? { all: [], held: [] }
      if (!tags.all.includes(part.wallet_address)) tags.all.push(part.wallet_address)
      if (mayHold(holding) && !tags.held.includes(part.wallet_address))
        tags.held.push(part.wallet_address)
      walletsOf.set(key, tags)
    }
  }
  const complete = parts.length >= viewed
  const holdings = [...byMaster.entries()].map(([key, holding]) => {
    const tags = walletsOf.get(key)
    return {
      ...holding,
      client_wallets: tags?.all ?? [],
      ...(complete ? { client_held_in: tags?.held ?? [] } : {}),
    }
  })
  return {
    ...first,
    wallet_address: parts.map((part) => part.wallet_address).join(','),
    holdings,
    holdings_count: holdings.length,
    total_current_value_ton: sumKnown(parts.map((part) => part.total_current_value_ton)),
    total_realized_pnl_ton: sumKnown(parts.map((part) => part.total_realized_pnl_ton)),
    total_unrealized_pnl_ton: sumKnown(parts.map((part) => part.total_unrealized_pnl_ton)),
    total_pnl_ton: sumKnown(parts.map((part) => part.total_pnl_ton)),
    total_txns: parts.reduce((sum, part) => sum + (part.total_txns ?? 0), 0),
    // one wallet's unknown total ("": positions server, a part not known)
    // leaves the sum unknown — never the other wallets' part as the whole
    total_current_value_usd: sumStated(parts.map((part) => part.total_current_value_usd)),
    total_realized_pnl_usd: sumStated(parts.map((part) => part.total_realized_pnl_usd)),
    total_unrealized_pnl_usd: sumStated(parts.map((part) => part.total_unrealized_pnl_usd)),
    total_pnl_usd: sumStated(parts.map((part) => part.total_pnl_usd)),
    price_coverage_complete: parts.every((part) => part.price_coverage_complete),
    ...(parts.some((part) => part.pnl_complete != null)
      ? { pnl_complete: parts.every((part) => part.pnl_complete !== false) }
      : {}),
    // one wallet's list not checked whole leaves the merged one not whole
    ...(parts.some((part) => part.holdings_complete === false) ? { holdings_complete: false } : {}),
    ...(parts.some((part) => part.holdings_unresolved != null)
      ? {
          holdings_unresolved: parts.reduce(
            (sum, part) => sum + (part.holdings_unresolved ?? 0),
            0
          ),
        }
      : {}),
  }
}
